import BigNumber from 'bignumber.js'
import { useQuery } from 'react-query'
import { useAtomValue } from 'jotai'
import { strideAccountAtom } from '@/atoms/Wallet'
import { queryKeys } from '@/query-keys'
import { stride } from 'stridejs'
import { STRIDE_CHAIN_INFO } from '@/config'
import { fatal, fromUnixTimestamp } from '@/utils'
import { Coin } from '@cosmjs/stargate'

export interface ClaimHistoryRecord {
  type: 'claim' | 'stake' | 'liquid-stake'
  startDate: number
  endDate: number
  total: number
}

export interface ClaimHistoryReturnType {
  records: ClaimHistoryRecord[]
}

const useClaimHistoryQuery = () => {
  const strideAccount = useAtomValue(strideAccountAtom)

  const handleRequest = async (): Promise<ClaimHistoryReturnType> => {
    if (!strideAccount) {
      throw fatal('Unable to fetch claim history while wallet is disconnected.')
    }

    const client = await stride.ClientFactory.createRPCQueryClient({
      rpcEndpoint: STRIDE_CHAIN_INFO.rpc
    })

    const vestings = await client.stride.claim.userVestings({
      address: strideAccount.key.bech32Address
    })

    return {
      records: vestings.periods.map((period) => {
        const record: ClaimHistoryRecord = {
          type: getPeriodActionType(period.actionType),
          startDate: fromUnixTimestamp(period.startTime.toNumber()),
          endDate: fromUnixTimestamp(period.startTime.toNumber()) + fromUnixTimestamp(period.length.toNumber()),
          total: getPeriodTotalDenom(period.amount)
        }

        return record
      })
    }
  }

  return useQuery(
    queryKeys.airdropClaimHistoryByAddress({ address: strideAccount?.key.bech32Address ?? '' }),
    handleRequest,
    { enabled: Boolean(strideAccount) }
  )
}

const getPeriodActionType = (type: number): ClaimHistoryRecord['type'] => {
  if (type === 2) return 'stake'
  if (type === 1) return 'liquid-stake'
  return 'claim'
}

const getPeriodTotalDenom = (amount: Coin[]): number => {
  const total = amount.reduce((total, value) => {
    return total.plus(value.amount)
  }, new BigNumber(0))

  return total.toNumber()
}

export { useClaimHistoryQuery }
